import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import AppNavbar from './components/AppNavbar';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ProductsPage from './pages/ProductsPage';
import OrderPage from './pages/OrderPage';
import ServicesPage from './pages/ServicesPage';
import PortfolioPage from './pages/PortfolioPage';
import ContactPage from './pages/ContactPage';
import FAQPage from './pages/FAQPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AdminDashboard from './pages/AdminDashboard';
import MyOrdersPage from './pages/MyOrdersPage';
import MyTasksPage from './pages/MyTasksPage';
import ProfilePage from './pages/ProfilePage';
import QuoteModal from './components/QuoteModal';
import WhatsAppWidget from './components/WhatsAppWidget';

import { useMutation } from '@apollo/client';
import { RECORD_LOGIN_ATTENDANCE } from './graphql/operations';
import LocationModal from './components/LocationModal';

const App: React.FC = () => {
  const location = useLocation();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);

  const [recordLoginAttendance] = useMutation(RECORD_LOGIN_ATTENDANCE);

  const openQuote = () => setQuoteOpen(true);
  const closeQuote = () => setQuoteOpen(false);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  // Sync auth state on mount and trigger location check for staff
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role') || 'customer';
    const isAuth = !!token;
    setIsAuthenticated(isAuth);

    if (isAuth && role !== 'customer') {
      const today = new Date().toISOString().slice(0, 10);
      const recordedDate = localStorage.getItem('attendance_location_date');
      if (recordedDate !== today) {
        setShowLocationModal(true);
      }
    }
  }, [location.pathname]);

  const handleLocationObtained = async (lat: number, lng: number, locStr: string) => {
    try {
      await recordLoginAttendance({
        variables: {
          latitude: lat,
          longitude: lng,
          location: locStr,
        },
      });
      const today = new Date().toISOString().slice(0, 10);
      localStorage.setItem('attendance_location_date', today);
    } catch (err) {
      console.error('Failed to record location attendance:', err);
    } finally {
      setShowLocationModal(false);
    }
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    const role = localStorage.getItem('role') || 'customer';
    if (role !== 'customer') {
      setShowLocationModal(true);
    }
  };

  return (
    <>
      <AppNavbar
        onGetQuote={openQuote}
        isAuthenticated={isAuthenticated}
        onLogout={() => setIsAuthenticated(false)}
      />

      <Routes>
        <Route path="/" element={<HomePage onGetQuote={openQuote} />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/order" element={<OrderPage />} />
        <Route path="/portfolio" element={<PortfolioPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/login" element={<LoginPage onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/admin-login" element={<LoginPage onLoginSuccess={handleLoginSuccess} isAdminLogin />} />
        <Route path="/register" element={<RegisterPage onRegisterSuccess={handleLoginSuccess} />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/my-orders" element={<MyOrdersPage />} />
        <Route path="/my-tasks" element={<MyTasksPage />} />
        <Route path="/profile" element={<ProfilePage />} />
      </Routes>

      {/* Forced Location Verification Modal for Staff Attendance */}
      <LocationModal
        show={showLocationModal}
        onLocationObtained={handleLocationObtained}
      />

      {/* Quote Modal */}
      <QuoteModal open={quoteOpen} onClose={closeQuote} />

      {/* Interactive WhatsApp Widget */}
      <WhatsAppWidget />

      {/* Floating Call Button */}
      <a
        className="call-fab"
        href="tel:+919150808130"
        title="Call Us"
      >
        <i className="fas fa-phone"></i>
      </a>
    </>
  );
};

export default App;
